import { apiClient, type Paginated } from '@/core/api';
import type { Contract, ContractInput } from './types';

export const BASE = '/crm/contracts/';

export interface ContractListParams {
  page?: number;
  search?: string;
  status?: string;
  company?: number;
  deal?: number;
  proposal?: number;
  /** `true` → only archived rows; `'all'` → both. Unset/`false` → active only. */
  archived?: boolean | 'all';
  /** DRF `OrderingFilter` — e.g. `created` or `-created`. */
  ordering?: string;
  /** Created-window (DateTime) — ISO bounds; `_before` should be end-of-day. */
  created_after?: string;
  created_before?: string;
}

export async function listContracts(params: ContractListParams = {}): Promise<Paginated<Contract>> {
  const { data } = await apiClient.get<Paginated<Contract>>(BASE, { params });
  return data;
}

export async function getContract(id: string | number): Promise<Contract> {
  const { data } = await apiClient.get<Contract>(`${BASE}${id}/`);
  return data;
}

export async function createContract(input: ContractInput): Promise<Contract> {
  const { data } = await apiClient.post<Contract>(BASE, input);
  return data;
}

export async function updateContract(id: string | number, input: Partial<ContractInput>): Promise<Contract> {
  const { data } = await apiClient.patch<Contract>(`${BASE}${id}/`, input);
  return data;
}

/**
 * Soft-archive a contract. The backend keeps the row, flips `is_archived=true`,
 * and hides it from default list queries. Use {@link restoreContract} to reverse.
 */
export async function archiveContract(id: string | number): Promise<void> {
  await apiClient.delete(`${BASE}${id}/`);
}

export async function restoreContract(id: string | number): Promise<Contract> {
  const { data } = await apiClient.post<Contract>(`${BASE}${id}/restore/`);
  return data;
}

/** Permanently delete an archived contract (hard delete; admin only, irreversible). */
export async function purgeContract(id: string | number): Promise<void> {
  await apiClient.delete(`${BASE}${id}/purge/`);
}

/** Sign the contract → backend marks it SIGNED, wins the deal, and provisions a
 *  Project (returned). Spine step Contract → Project. */
export async function signContract(
  id: string | number,
  input: { project_name?: string } = {},
): Promise<import('@/features/projects/types').Project> {
  const { data } = await apiClient.post(`${BASE}${id}/sign/`, input);
  return data as import('@/features/projects/types').Project;
}
